import React, { useState } from 'react';
import { Building2, Send, RefreshCw } from 'lucide-react';
import apiClient from '../../utils/apiClient';

// Platform staff (admin OR support) provision a customer: an Organization
// + an invited portfolio_owner. Owner gets an email link, activates, and
// signs in with an email code (passwordless). Owner then creates their own
// facilities/staff.
const ProvisionOrgPanel = () => {
  const [form, setForm] = useState({ org_name: '', owner_full_name: '', owner_email: '' });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.org_name.trim() || !form.owner_full_name.trim() || !form.owner_email.trim()) {
      setError('All fields are required.');
      return;
    }
    setWorking(true);
    try {
      const res = await apiClient.post('/api/platform/organizations', {
        org_name: form.org_name.trim(),
        owner_full_name: form.owner_full_name.trim(),
        owner_email: form.owner_email.trim().toLowerCase(),
      });
      setResult(res.data);
      setForm({ org_name: '', owner_full_name: '', owner_email: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not provision organization.');
    } finally {
      setWorking(false);
    }
  };

  const resend = async () => {
    if (!result?.organization_id) return;
    setWorking(true);
    try {
      const res = await apiClient.post(
        `/api/platform/organizations/${result.organization_id}/resend-invite`
      );
      setResult({ ...result, invite_email_sent: res.data.invite_email_sent });
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not resend invite.');
    } finally {
      setWorking(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all';

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800 p-6 max-w-xl">
      <div className="flex items-center gap-2 mb-1">
        <Building2 className="h-4 w-4 text-slate-400" />
        <h3 className="text-[15px] font-semibold text-slate-900 dark:text-slate-100">Provision organization</h3>
      </div>
      <p className="text-[13px] text-slate-400 mb-5">
        Creates a customer org + an invited owner. The owner gets an email to
        activate and signs in with a code — they then add their own
        facilities and staff.
      </p>

      {result ? (
        <div className="rounded-lg border border-emerald-200/80 bg-emerald-50 p-4 text-[13px] text-emerald-800 space-y-2">
          <p className="font-medium">Organization created.</p>
          <p>Owner: {result.owner?.full_name} ({result.owner?.email})</p>
          <p>
            Invite email:{' '}
            {result.invite_email_sent ? 'sent ✓' : 'NOT sent (resend below)'}
          </p>
          <div className="flex gap-2 pt-1">
            <button
              onClick={resend}
              disabled={working}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-emerald-300 text-[13px] font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Resend invite
            </button>
            <button
              onClick={() => setResult(null)}
              className="px-3 py-1.5 rounded-md text-[13px] font-medium text-slate-500 hover:text-slate-700"
            >
              Provision another
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200/80 px-3 py-2.5 text-[13px] text-red-600">
              {error}
            </div>
          )}
          <div>
            <label className="block text-[12px] font-medium text-slate-500 mb-1">Organization name</label>
            <input className={inputCls} value={form.org_name} onChange={set('org_name')} placeholder="e.g. Acme Storage" disabled={working} />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-slate-500 mb-1">Owner full name</label>
            <input className={inputCls} value={form.owner_full_name} onChange={set('owner_full_name')} placeholder="Owner's name" disabled={working} />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-slate-500 mb-1">Owner email</label>
            <input type="email" className={inputCls} value={form.owner_email} onChange={set('owner_email')} placeholder="owner@example.com" disabled={working} />
          </div>
          <button
            type="submit"
            disabled={working}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <Send className="h-3.5 w-3.5" /> {working ? 'Provisioning…' : 'Provision & invite owner'}
          </button>
        </form>
      )}
    </div>
  );
};

export default ProvisionOrgPanel;
